import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  rotation: number;
  scale: number;
  velocityX: number;
  velocityY: number;
  type: 'confetti' | 'star' | 'sparkle';
}

const COLORS = ['#FF6B6B', '#FFE66D', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE'];
const EMOJIS = ['🏆', '⭐', '🌟', '✨', '💫', '🔥', '💥', '🎯', '🎊', '🎉'];

export function VictoryCelebration() {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [burstCount, setBurstCount] = useState(0);

  // Create explosion burst
  useEffect(() => {
    const createBurst = () => {
      const newParticles: Particle[] = [];
      const particleCount = 60;
      
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2 + Math.random() * 0.5;
        const velocity = 8 + Math.random() * 12;
        
        newParticles.push({
          id: Date.now() + i,
          x: 50,
          y: 50,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          rotation: Math.random() * 360,
          scale: 0.5 + Math.random() * 1,
          velocityX: Math.cos(angle) * velocity,
          velocityY: Math.sin(angle) * velocity,
          type: Math.random() > 0.7 ? 'star' : Math.random() > 0.5 ? 'sparkle' : 'confetti',
        });
      }
      
      setParticles((prev) => [...prev, ...newParticles]);
      setBurstCount((c) => c + 1);
    };

    // Initial burst
    createBurst();
    
    // Additional bursts
    const timer1 = setTimeout(createBurst, 300);
    const timer2 = setTimeout(createBurst, 600);
    const timer3 = setTimeout(createBurst, 1200);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // Animate particles
  useEffect(() => {
    if (particles.length === 0) return;

    const interval = setInterval(() => {
      setParticles((prev) =>
        prev
          .map((p) => ({
            ...p,
            x: p.x + p.velocityX * 0.3,
            y: p.y + p.velocityY * 0.3,
            velocityY: p.velocityY + 0.3, // gravity
            velocityX: p.velocityX * 0.99, // friction
            rotation: p.rotation + 5,
            scale: p.scale * 0.995,
          }))
          .filter((p) => p.y < 150 && p.scale > 0.1)
      );
    }, 16);

    return () => clearInterval(interval);
  }, [particles.length > 0]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {/* CSS Animations */}
      <style>{`
        @keyframes trophyPulse {
          0%, 100% { transform: scale(1) rotate(-5deg); filter: drop-shadow(0 0 20px gold); }
          25% { transform: scale(1.1) rotate(5deg); filter: drop-shadow(0 0 40px gold); }
          50% { transform: scale(1.2) rotate(-3deg); filter: drop-shadow(0 0 60px gold); }
          75% { transform: scale(1.1) rotate(3deg); filter: drop-shadow(0 0 40px gold); }
        }
        @keyframes victoryText {
          0%, 100% { transform: scale(1); text-shadow: 0 0 20px rgba(255,107,107,0.8); }
          50% { transform: scale(1.05); text-shadow: 0 0 40px rgba(255,107,107,1), 0 0 80px rgba(255,107,107,0.6); }
        }
        @keyframes starBurst {
          0% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
          100% { opacity: 0; transform: scale(0) rotate(360deg); }
        }
        @keyframes ringExpand {
          0% { transform: scale(0); opacity: 1; }
          100% { transform: scale(3); opacity: 0; }
        }
        @keyframes floatEmoji {
          0%, 100% { transform: translate(-50%, -50%) translateY(0) rotate(-10deg); }
          50% { transform: translate(-50%, -50%) translateY(-20px) rotate(10deg); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes explosionFlash {
          0% { opacity: 0.8; transform: scale(0.5); }
          100% { opacity: 0; transform: scale(2); }
        }
      `}</style>

      {/* Explosion rings */}
      {burstCount > 0 && (
        <>
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border-4 border-yellow-400"
            style={{ animation: 'ringExpand 1s ease-out forwards' }}
          />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border-4 border-red-400"
            style={{ animation: 'ringExpand 1s ease-out 0.15s forwards' }}
          />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border-4 border-cyan-400"
            style={{ animation: 'ringExpand 1s ease-out 0.3s forwards' }}
          />
        </>
      )}

      {/* Explosion flash */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-radial from-yellow-400/60 via-orange-500/30 to-transparent"
        style={{ animation: 'explosionFlash 0.6s ease-out forwards' }}
      />

      {/* Floating emojis around edges */}
      {EMOJIS.map((emoji, i) => (
        <div
          key={i}
          className="absolute text-4xl"
          style={{
            left: `${10 + (i % 5) * 20}%`,
            top: `${i < 5 ? 8 : 92}%`,
            animation: `floatEmoji ${1.5 + Math.random()}s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
          }}
        >
          {emoji}
        </div>
      ))}

      {/* Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
          }}
        >
          {particle.type === 'confetti' && (
            <div
              className="w-3 h-4 rounded-sm"
              style={{ backgroundColor: particle.color }}
            />
          )}
          {particle.type === 'star' && (
            <div className="text-2xl" style={{ color: particle.color }}>
              ★
            </div>
          )}
          {particle.type === 'sparkle' && (
            <div className="text-xl" style={{ color: particle.color }}>
              ✦
            </div>
          )}
        </div>
      ))}

      {/* Corner sparkles */}
      {[0, 1, 2, 3].map((corner) => (
        <div
          key={corner}
          className="absolute text-6xl"
          style={{
            left: corner % 2 === 0 ? '5%' : '85%',
            top: corner < 2 ? '10%' : '80%',
            animation: `starBurst 2s ease-in-out infinite`,
            animationDelay: `${corner * 0.5}s`,
          }}
        >
          ✨
        </div>
      ))}
    </div>
  );
}

interface VictoryHeaderProps {
  title?: string;
  subtitle?: string;
}

export function VictoryHeader({ title = 'HUNT COMPLETE!', subtitle }: VictoryHeaderProps) {
  return (
    <div className="text-center relative z-10">
      {/* Trophy */}
      <div
        className="text-8xl mb-4"
        style={{ animation: 'trophyPulse 2s ease-in-out infinite' }}
      >
        🏆
      </div>
      
      {/* Victory text with shimmer effect */}
      <h1
        className="text-4xl md:text-5xl font-black tracking-wider mb-2"
        style={{
          animation: 'victoryText 1.5s ease-in-out infinite',
          background: 'linear-gradient(90deg, #FF6B6B, #FFE66D, #FF6B6B, #FFE66D, #FF6B6B)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animationName: 'shimmer, victoryText',
          animationDuration: '3s, 1.5s',
          animationTimingFunction: 'linear, ease-in-out',
          animationIterationCount: 'infinite, infinite',
        }}
      >
        {title}
      </h1>
      
      {subtitle && (
        <p className="text-gray-300 text-lg animate-pulse">{subtitle}</p>
      )}
    </div>
  );
}
